import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar, Toolbar, Typography, Button, Box, IconButton, Tooltip,
  useTheme, Chip, Menu, MenuItem, Divider, Avatar, Paper,
  BottomNavigation, BottomNavigationAction, ListItemIcon, Badge,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import CompassIcon from '@mui/icons-material/Explore';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from '../context/AuthContext';
import { getHomeDashboard } from '../services/api';

// Nav items — keep in sync with App.js protected routes. Labels are loaded
// from i18n at render time (see useTranslation below) so we only define the
// route + icon here.
//
// Order matters: Home is first because it's the mission-control surface
// established users land on. Jobs / Applications / Resume sit in the order
// of a typical journey (find → track → revise).
const NAV_ITEM_KEYS = [
  { tKey: 'home',         shortKey: 'home',              path: '/',          icon: <HomeIcon />        },
  { tKey: 'jobs',         shortKey: 'jobs',              path: '/jobs',      icon: <SearchIcon />      },
  { tKey: 'applications', shortKey: 'applications_short', path: '/dashboard', icon: <DashboardIcon />   },
  { tKey: 'resume',       shortKey: 'resume',            path: '/resume',    icon: <DescriptionIcon /> },
];

// Height of the bottom nav bar, used as a spacer so the last element on every
// page doesn't disappear under the fixed bar.
const BOTTOM_NAV_HEIGHT = 56;

// Languages we support. Order is what the menu shows.
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'pt', label: 'Português' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const { user, signOut } = useAuth();

  // Dashboard data drives the badges on Applications + Resume nav items.
  // We fetch once on mount and on route change (so badges update after the
  // user saves/applies/uploads). Failure is silent — badges just don't show.
  const [navData, setNavData] = useState(null);
  useEffect(() => {
    if (!user) { setNavData(null); return; }
    let cancelled = false;
    getHomeDashboard()
      .then((res) => { if (!cancelled) setNavData(res.data); })
      .catch(() => { if (!cancelled) setNavData(null); });
    return () => { cancelled = true; };
  }, [user, location.pathname]);

  // Compute per-item badges. Returns string|null per nav path.
  // - Applications: count of "in flight" apps (saved+applied+interview).
  //   Hidden when 0; capped at "9+" for visual cleanliness.
  // - Resume: "!" warning when no resume on file. Otherwise no badge.
  // - Home and Jobs have no badges in v1 — adding them later requires
  //   richer signals (unread matches, etc.) we don't track yet.
  const badgesByPath = (() => {
    if (!navData) return {};
    const p = navData.pipeline || {};
    const active = (p.saved || 0) + (p.applied || 0) + (p.interview_scheduled || 0);
    const result = {};
    if (active > 0) {
      result['/dashboard'] = active > 9 ? '9+' : String(active);
    }
    if (navData.has_resume === false) {
      result['/resume'] = '!';
    }
    return result;
  })();

  // Build localized nav items + title-by-path map at render time so a
  // language switch updates the labels without a page reload.
  const navItems = NAV_ITEM_KEYS.map(item => ({
    ...item,
    label:      t(`nav.${item.tKey}`),
    shortLabel: t(`nav.${item.shortKey}`),
  }));
  const titleByPath = Object.fromEntries(navItems.map(i => [i.path, i.label]));
  titleByPath['/settings'] = t('nav.settings');

  const handleNav = (path) => navigate(path);

  const handleSignOut = async () => {
    setUserMenuAnchor(null);
    await signOut();
    navigate('/login', { replace: true });
  };

  const handleChangeLanguage = (code) => {
    i18n.changeLanguage(code);
    setLangMenuAnchor(null);
  };

  const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

  // First letter of email — used for the avatar circle.
  const initial = (user?.email || '?').charAt(0).toUpperCase();

  // Find which nav item (if any) matches the current route so the bottom nav
  // can highlight it. Nested routes like /tailor/:id don't match any item,
  // in which case nothing is highlighted — that's intentional.
  const activeNavValue = navItems.find(i => i.path === location.pathname)?.path ?? null;

  // ─── Shared user menu (avatar → popover) ──────────────────────────────────
  const userMenu = (
    <>
      <IconButton
        onClick={(e) => setUserMenuAnchor(e.currentTarget)}
        size="small"
        aria-label={t('nav.user_menu')}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
          {initial}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box px={2} py={1}>
          <Typography variant="caption" color="text.secondary">
            {t('nav.signed_in_as')}
          </Typography>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 240 }}>
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        {/* Language picker — submenu trigger. We open a separate Menu anchored
            to the same trigger element below to keep the markup simple. */}
        <MenuItem
          onClick={(e) => {
            setLangMenuAnchor(e.currentTarget);
          }}
        >
          <ListItemIcon><LanguageIcon fontSize="small" /></ListItemIcon>
          {LANGUAGES.find(l => l.code === currentLang)?.label || 'English'}
        </MenuItem>
        <MenuItem onClick={() => { setUserMenuAnchor(null); handleNav('/settings'); }}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          {t('nav.settings')}
        </MenuItem>
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          {t('nav.sign_out')}
        </MenuItem>
      </Menu>

      {/* Language sub-menu, anchored to whichever element opened it. */}
      <Menu
        anchorEl={langMenuAnchor}
        open={Boolean(langMenuAnchor)}
        onClose={() => setLangMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {LANGUAGES.map(({ code, label }) => (
          <MenuItem
            key={code}
            onClick={() => {
              handleChangeLanguage(code);
              setUserMenuAnchor(null);
            }}
            selected={code === currentLang}
          >
            <ListItemIcon>
              {code === currentLang ? <CheckIcon fontSize="small" /> : <Box sx={{ width: 20 }} />}
            </ListItemIcon>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  return (
    <>
      {/* ─── Top bar ──────────────────────────────────────────────────────
          Same structure on mobile and desktop. On mobile only the avatar
          appears on the right (navigation lives in the bottom nav). On
          desktop the 3 nav items sit inline between logo and avatar. */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Toolbar>
          {/* Compass icon only — click returns to the Home (mission-control)
              surface. The wordmark "Career Compass" used to live here but took
              too much horizontal space; the icon alone keeps identity without
              the clutter. */}
          <Tooltip title="Career Compass">
            <IconButton
              onClick={() => handleNav('/')}
              sx={{ mr: 1, p: 0.75 }}
              aria-label={t('nav.logo_alt')}
            >
              <CompassIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            </IconButton>
          </Tooltip>

          {/* Page title — only on mobile (xs). On desktop the active inline
              nav item already signals which page the user is on, so showing
              both creates redundancy and clutter. Mobile keeps it because
              the bottom-nav highlight isn't always in the user's eye line. */}
          {titleByPath[location.pathname] && (
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="text.primary"
              sx={{
                display: { xs: 'block', sm: 'none' },
                letterSpacing: '-0.2px',
                fontSize: 15,
                mr: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {titleByPath[location.pathname]}
            </Typography>
          )}

          {/* Desktop-only inline nav (hidden on xs). All 4 items always
              shown — active item gets a distinct visual state instead of
              being filtered out, which avoids the jarring "items appear
              and disappear as you navigate" behavior. Badges overlay on
              the upper-right of each button. */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5 }}>
            {navItems.map(item => {
              const isActive = item.path === location.pathname;
              const badgeContent = badgesByPath[item.path];
              const isWarning = badgeContent === '!';
              const button = (
                <Button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  disableRipple
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    minWidth: 'auto',
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? 'primary.main' : 'text.secondary',
                    bgcolor: isActive ? 'rgba(10,37,64,0.06)' : 'transparent',
                    '&:hover': {
                      bgcolor: isActive ? 'rgba(10,37,64,0.10)' : 'rgba(10,37,64,0.04)',
                      color: 'primary.main',
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
              if (!badgeContent) return button;
              return (
                <Badge
                  key={item.path}
                  badgeContent={badgeContent}
                  color={isWarning ? 'warning' : 'primary'}
                  overlap="rectangular"
                  sx={{
                    '& .MuiBadge-badge': {
                      top: 6,
                      right: 6,
                      fontSize: 10,
                      height: 16,
                      minWidth: 16,
                      px: 0.5,
                    },
                  }}
                >
                  {button}
                </Badge>
              );
            })}
          </Box>

          {/* Flexible spacer pushes the avatar to the right edge */}
          <Box flex={1} />

          {/* Beta pill — small, muted, stays visible next to the avatar so
              there's still a signal that this is an unfinished product. */}
          <Chip
            label={t('common.beta')}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 18, fontSize: 10, mr: 1 }}
          />

          {user && userMenu}
        </Toolbar>
      </AppBar>

      {/* ─── Mobile bottom navigation (hidden on sm+) ─────────────────────
          Fixed to viewport bottom. Thumb-friendly tap targets. The spacer
          rendered at the end of this component prevents page content from
          being obscured by the bar. */}
      <Paper
        component="nav"
        elevation={8}
        sx={{
          display: { xs: 'block', sm: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          borderTop: '1px solid',
          borderColor: 'divider',
          // Respect iOS home indicator safe area on modern iPhones.
          pb: 'env(safe-area-inset-bottom)',
        }}
        aria-label="Main navigation"
      >
        <BottomNavigation
          showLabels
          value={activeNavValue}
          onChange={(_e, newValue) => newValue && navigate(newValue)}
          sx={{
            height: BOTTOM_NAV_HEIGHT,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              color: 'text.secondary',
              '&.Mui-selected': { color: 'primary.main' },
            },
          }}
        >
          {navItems.map(item => {
            const badgeContent = badgesByPath[item.path];
            const isWarning = badgeContent === '!';
            // Wrap the icon in a Badge when there's something to surface;
            // for the no-badge case we pass the bare icon so the layout
            // height is identical (avoids 1-2px shift between rendered states).
            const iconNode = badgeContent
              ? (
                <Badge
                  badgeContent={badgeContent}
                  color={isWarning ? 'warning' : 'primary'}
                  overlap="circular"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: 9,
                      height: 14,
                      minWidth: 14,
                      px: 0.5,
                    },
                  }}
                >
                  {item.icon}
                </Badge>
              )
              : item.icon;
            return (
              <BottomNavigationAction
                key={item.path}
                label={item.shortLabel || item.label}
                value={item.path}
                icon={iconNode}
                sx={{
                  // Prevent label wrapping to a second line on narrow widths
                  // (keeps all 4 items aligned at the same visual height).
                  '& .MuiBottomNavigationAction-label': {
                    whiteSpace: 'nowrap',
                    fontSize: 11,
                  },
                }}
              />
            );
          })}
        </BottomNavigation>
      </Paper>

      {/* Note: page content must reserve space so the fixed bottom nav
          doesn't obscure it. That's done in App.js AppShell by padding the
          <main> element on mobile breakpoints — see BOTTOM_NAV_HEIGHT_PX
          constant there. */}
    </>
  );
}

// Exported so App.js can match the spacer height without drifting.
export { BOTTOM_NAV_HEIGHT };
